import { c as _c } from "react/compiler-runtime";
import { IcoDotsHorizontal16 } from '@onefootprint/icons';
import type { PlaybookVersionNoGraph } from '@onefootprint/request-types/dashboard';
import { Dropdown, IconButton, Tooltip } from '@onefootprint/ui';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import usePermissions from '@/hooks/use-permissions';
import useSession from '@/hooks/use-session';
import type { CopyHandler } from './components/copy';
import Copy from './components/copy';
import type { CopyLinkHandler } from './components/copy-link-dialog';
import CopyLinkDialog from './components/copy-link-dialog';
import type { StatusHandler } from './components/status';
import Status from './components/status';
type ActionsProps = {
  playbook: PlaybookVersionNoGraph;
};
const Actions = t0 => {
  const $ = _c(59);
  const {
    playbook
  } = t0;
  const {
    name,
    status,
    kind
  } = playbook;
  const [dropdownOpen, setDropdownOpen] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "table.actions"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbooks", t1);
  const statusRef = useRef(null);
  const copyRef = useRef(null);
  const copyLinkRef = useRef(null);
  const canShowLink = kind === "kyc" || kind === "kyb" || kind === "document";
  const {
    data: t2
  } = useSession();
  const {
    user
  } = t2;
  const {
    hasPermission
  } = usePermissions();
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setDropdownOpen(false);
      statusRef.current?.toggle();
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const handleToggleStatus = t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setDropdownOpen(false);
      copyRef.current?.launch();
    };
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const launchCopy = t4;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setDropdownOpen(false);
      copyLinkRef.current?.launch();
    };
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const copyLinkToClipboard = t5;
  let t6;
  if ($[4] !== hasPermission || $[5] !== user?.isCxOpsEmployee || $[6] !== user?.isRiskOpsEmployee) {
    t6 = hasPermission("onboarding_configuration") || user?.isRiskOpsEmployee || user?.isCxOpsEmployee;
    $[4] = hasPermission;
    $[5] = user?.isCxOpsEmployee;
    $[6] = user?.isRiskOpsEmployee;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const canManagePlaybook = t6;
  let t7;
  if ($[8] !== t) {
    t7 = t(_temp3);
    $[8] = t;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const t8 = !canManagePlaybook;
  let t9;
  if ($[10] !== name || $[11] !== t) {
    t9 = t(_temp4, {
      name
    });
    $[10] = name;
    $[11] = t;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const t10 = !canManagePlaybook;
  let t11;
  if ($[13] !== t10 || $[14] !== t9) {
    t11 = <IconButton aria-label={t9} size="tiny" icon={IcoDotsHorizontal16} disabled={t10} className="max-md:size-11 active:bg-senary!" />;
    $[13] = t10;
    $[14] = t9;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t11 || $[17] !== t8) {
    t12 = <Dropdown.Trigger disabled={t8} asChild={true}>{t11}</Dropdown.Trigger>;
    $[16] = t11;
    $[17] = t8;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== canManagePlaybook || $[20] !== t12 || $[21] !== t7) {
    t13 = <Tooltip content={t7} disabled={canManagePlaybook}>{t12}</Tooltip>;
    $[19] = canManagePlaybook;
    $[20] = t12;
    $[21] = t7;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== canShowLink || $[24] !== t) {
    t14 = canShowLink && <Dropdown.Item onSelect={copyLinkToClipboard} onClick={_temp5} size="compact">{t(_temp6)}</Dropdown.Item>;
    $[23] = canShowLink;
    $[24] = t;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  const t15 = !canManagePlaybook;
  let t16;
  if ($[26] !== t) {
    t16 = t(_temp8);
    $[26] = t;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== t15 || $[29] !== t16) {
    t17 = <Dropdown.Item onSelect={launchCopy} onClick={_temp7} disabled={t15} size="compact">{t16}</Dropdown.Item>;
    $[28] = t15;
    $[29] = t16;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== t14 || $[32] !== t17) {
    t18 = <Dropdown.Group>{t14}{t17}</Dropdown.Group>;
    $[31] = t14;
    $[32] = t17;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <Dropdown.Divider />;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  const t20 = playbook.status === "enabled";
  let t21;
  if ($[35] !== playbook.status || $[36] !== t) {
    t21 = playbook.status === "enabled" ? t(_temp10) : t(_temp11);
    $[35] = playbook.status;
    $[36] = t;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  let t22;
  if ($[38] !== t20 || $[39] !== t21) {
    t22 = <Dropdown.Group><Dropdown.Item onSelect={handleToggleStatus} onClick={_temp9} destructive={t20} size="compact">{t21}</Dropdown.Item></Dropdown.Group>;
    $[38] = t20;
    $[39] = t21;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  let t23;
  if ($[41] !== t18 || $[42] !== t22) {
    t23 = <Dropdown.Portal><Dropdown.Content align="end">{t18}{t19}{t22}</Dropdown.Content></Dropdown.Portal>;
    $[41] = t18;
    $[42] = t22;
    $[43] = t23;
  } else {
    t23 = $[43];
  }
  let t24;
  if ($[44] !== dropdownOpen || $[45] !== t13 || $[46] !== t23) {
    t24 = <Dropdown.Root open={dropdownOpen} onOpenChange={setDropdownOpen}>{t13}{t23}</Dropdown.Root>;
    $[44] = dropdownOpen;
    $[45] = t13;
    $[46] = t23;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  let t25;
  if ($[48] !== playbook || $[49] !== status) {
    t25 = <Status playbook={playbook} key={status} ref={statusRef} />;
    $[48] = playbook;
    $[49] = status;
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  let t26;
  let t27;
  if ($[51] !== playbook) {
    t26 = <CopyLinkDialog playbook={playbook} ref={copyLinkRef} />;
    t27 = <Copy playbook={playbook} ref={copyRef} />;
    $[51] = playbook;
    $[52] = t26;
    $[53] = t27;
  } else {
    t26 = $[52];
    t27 = $[53];
  }
  let t28;
  if ($[54] !== t24 || $[55] !== t25 || $[56] !== t26 || $[57] !== t27) {
    t28 = <div className="flex flex-end" onClick={_temp} onKeyUp={_temp2}>{t24}{t25}{t26}{t27}</div>;
    $[54] = t24;
    $[55] = t25;
    $[56] = t26;
    $[57] = t27;
    $[58] = t28;
  } else {
    t28 = $[58];
  }
  return t28;
};
export default Actions;
function _temp(e) {
  return e.stopPropagation();
}
function _temp2(e_0) {
  return e_0.stopPropagation();
}
function _temp3($) {
  return $.notAllowed;
}
function _temp4($_0) {
  return $_0.ariaLabel;
}
function _temp5(event) {
  return event.stopPropagation();
}
function _temp6($_1) {
  return $_1.getLink;
}
function _temp7(event_0) {
  return event_0.stopPropagation();
}
function _temp8($_2) {
  return $_2.copy;
}
function _temp9(event_1) {
  return event_1.stopPropagation();
}
function _temp10($_3) {
  return $_3.status.disable.cta;
}
function _temp11($_4) {
  return $_4.status.enable.cta;
}